/**
 * LeadKitchen React dashboard — API client.
 *
 * Thin fetch wrapper around the FastAPI backend.
 * - Reads JWT from localStorage under key `lk_token` and sends it as `Authorization: Bearer ...`.
 * - On 401: clears token and force-redirects the browser to the React login page.
 * - Throws `ApiError` with `status` and parsed `body` for non-OK responses.
 *
 * Path argument is relative to `/api`, e.g. `apiFetch('/auth/me')` hits `/api/auth/me`.
 * See docs/react-migration/00-architecture.md §3.4.
 */
import { getCurrentDashboardBasePath } from '../utils/dashboardBasePath';

const BASE_URL = '/api';
export const TOKEN_STORAGE_KEY = 'lk_token';
export const WORKSPACE_SLUG_STORAGE_KEY = 'lk_workspace_slug';

function getLoginPath() {
  return `${getCurrentDashboardBasePath()}/login`;
}

export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setToken(token) {
  try {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch {
    /* noop — private mode or disabled storage */
  }
}

export function clearToken() {
  setToken(null);
  try {
    localStorage.removeItem(WORKSPACE_SLUG_STORAGE_KEY);
  } catch {
    /* noop */
  }
}

function getWorkspaceSlug() {
  try {
    const slug = localStorage.getItem(WORKSPACE_SLUG_STORAGE_KEY);
    return slug && slug.trim() ? slug.trim() : null;
  } catch {
    return null;
  }
}

function isUnscopedRequest(path) {
  return path.startsWith('/auth/') || path === '/my/sites';
}

async function parseBody(resp) {
  const contentType = resp.headers.get('content-type') || '';
  try {
    if (contentType.includes('application/json')) {
      return await resp.json();
    }
    const text = await resp.text();
    return text || null;
  } catch {
    return null;
  }
}

function localizeApiDetail(detail) {
  if (detail && typeof detail === 'object') {
    if (detail.code === 'SITE_SELECTION_REQUIRED') {
      return 'Выберите кабинет и повторите действие';
    }
    if (detail.code === 'SITE_NOT_FOUND') {
      return 'Выбранный кабинет недоступен';
    }
    return detail.message || detail.code || 'Ошибка запроса';
  }
  if (typeof detail !== 'string') return detail;
  const domainConflict = detail.match(/^Domain '([^']+)' is already assigned to '([^']+)'$/);
  if (domainConflict) {
    const [, domain, owner] = domainConflict;
    return `Домен ${domain} уже привязан к кабинету «${owner}». Удалите его там или выберите другой домен.`;
  }
  if (detail === 'Invalid domain format') {
    return 'Проверьте формат домена. Например: example.ru';
  }
  return detail;
}

/**
 * Core fetch wrapper.
 *
 * @param {string} path  API path starting with `/`, resolved against `/api`.
 * @param {object} [options]  standard fetch options. `body` may be:
 *   - string: sent as-is, Content-Type: application/json is assumed.
 *   - FormData / URLSearchParams: sent as-is without forcing JSON content-type.
 *   - plain object: auto-serialised to JSON.
 * @param {boolean} [options.skipAuthRedirect]  if true, a 401 will NOT redirect — used by
 *   `me()` on app mount so we can silently clear a stale token without bouncing the user.
 */
export async function apiFetch(path, options = {}) {
  const { skipAuthRedirect = false, headers: extraHeaders, body, ...rest } = options;

  const headers = { ...(extraHeaders || {}) };
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const workspaceSlug = getWorkspaceSlug();
  if (
    workspaceSlug &&
    !isUnscopedRequest(path) &&
    !headers['X-Site-Slug'] &&
    !headers['x-site-slug']
  ) {
    headers['X-Site-Slug'] = workspaceSlug;
  }

  let finalBody = body;
  if (body !== undefined && body !== null) {
    if (body instanceof FormData || body instanceof URLSearchParams) {
      // let the browser set Content-Type (with boundary for FormData)
    } else if (typeof body === 'string') {
      if (!headers['Content-Type'] && !headers['content-type']) {
        headers['Content-Type'] = 'application/json';
      }
    } else {
      finalBody = JSON.stringify(body);
      if (!headers['Content-Type'] && !headers['content-type']) {
        headers['Content-Type'] = 'application/json';
      }
    }
  }

  const resp = await fetch(`${BASE_URL}${path}`, { ...rest, headers, body: finalBody });

  if (resp.status === 401) {
    clearToken();
    if (!skipAuthRedirect && typeof window !== 'undefined') {
      const loginPath = getLoginPath();
      const currentPath = window.location.pathname.replace(/\/+$/, '');
      if (currentPath !== loginPath) {
        window.location.href = loginPath;
      }
    }
    const errBody = await parseBody(resp);
    throw new ApiError('Unauthorized', 401, errBody);
  }

  if (!resp.ok) {
    const errBody = await parseBody(resp);
    const rawDetail =
      (errBody && typeof errBody === 'object' && (errBody.detail || errBody.message)) ||
      `HTTP ${resp.status}`;
    const detail = localizeApiDetail(rawDetail);
    throw new ApiError(detail, resp.status, errBody);
  }

  if (resp.status === 204) return null;
  return parseBody(resp);
}
